import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Box, Card, Theme, Typography } from "@mui/material";
import { createStyles, makeStyles } from "@mui/styles";
import { Layout } from "../Layout";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useRouting } from "JS/React/Hooks/Routes";
import { AppHeader } from "JS/React/Components/AppHeader";
import sortableHeader from "JS/React/HOC/SortableHeader";
import { ColumnSort, getColumnSort, sortData, Sorting } from "JS/Types";
import { GiftCategories } from ".";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AllTabs } from "JS/Models";
import { messaging } from "JS/Helpers/UserMessaging";
import { AppBackdropProgress } from "JS/React/Components/Progress/AppBackdropProgress";
import { useGlobalNavStack } from "JS/React/Hooks/NavStack";
import qs from "qs";
import { useGiftsGiven } from "JS/React/Hooks/Gifts";

const SortableHeader = sortableHeader<HTMLSpanElement>("span");

export const GiftsGivenListing = (props: any) => {
    const params: any = useParams();
    const { categoryId } = params;
    const Tabs: any = AllTabs;
    const classes = useStyles(props);
    const { className, ...rest } = props;
    const { homeStack } = useGlobalNavStack();
    const history = useHistory();
    const { linkProvider } = useRouting();
    const itemRefs = useRef([]);
    const { scrollTo } = useQueryParams();
    const {
        setGlobalGiftsCategoryStack,
        resetGlobalGiftsCategoryStack,
        giftsStack,
    } = useGlobalNavStack();
    const [searchQuery, setSearchQuery] = useState<string>(
        giftsStack[categoryId]?.searchQuery,
    );
    const { loading, loaded, filteredMembers } = useGiftsGiven(
        "members",
        searchQuery,
    );
    const [sorting, setSorting] = useState<Sorting>(
        giftsStack[categoryId]?.sorting,
    );

    useEffect(() => {
        if (
            !loading &&
            finalData?.length &&
            +scrollTo >= 0 &&
            itemRefs?.current[+scrollTo]
        ) {
            itemRefs.current[+scrollTo - 6]?.scrollIntoView({
                behavior: "instant",
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollTo, loading]);
    const onSortChange = (columnSort: ColumnSort, oldOrder: number) => {
        setSorting([columnSort]);
        setGlobalGiftsCategoryStack(
            {
                sorting: [columnSort],
            },
            categoryId,
        );
    };

    const finalData = useMemo(() => {
        return sortData(filteredMembers, sorting);
    }, [filteredMembers, sorting]);

    useEffect(() => {
        setGlobalGiftsCategoryStack(
            {
                searchQuery: searchQuery,
            },
            categoryId,
        );
    }, [filteredMembers]);

    useEffect(() => {
        if (giftsStack[categoryId]?.scrollPosition === 0) {
            window.scrollTo(0, 500);
            window.scrollTo(0, 0);
        }
    }, []);

    return (
        <Layout>
            <div className={clsx(className, classes.root)} {...rest}>
                <AppHeader
                    title={"Gifts Given"}
                    canGoBack
                    searchable={categoryId !== GiftCategories.SEND_MEMBER}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    searchPlaceholder={`Search Members by Name, LTD ID`}
                    onBackClick={() => {
                        if (!homeStack?.isHomeTrack) {
                            resetGlobalGiftsCategoryStack(categoryId);
                            resetGlobalGiftsCategoryStack(
                                `${categoryId}-content`,
                            );
                            history.push(linkProvider.react.gifts().index());
                        } else history.replace(linkProvider.react.home());
                    }}
                />

                <Box
                    display={"flex"}
                    justifyContent={"space-around"}
                    marginBottom={"22px"}
                >
                    <SortableHeader
                        upDirection="asc"
                        downDirection="desc"
                        sort={getColumnSort(sorting, {
                            col: "first_name",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>A-Z</span>
                    </SortableHeader>
                    <SortableHeader
                        upDirection="asc"
                        downDirection="desc"
                        sort={getColumnSort(sorting, {
                            col: "member_id",
                            position: sorting.length,
                        })}
                        onSortChanged={onSortChange}
                    >
                        <span>LTD ID</span>
                    </SortableHeader>
                </Box>

                {loaded &&
                    finalData?.map((u, idx) => (
                        <Card
                            key={idx}
                            className={clsx(classes.itemCard)}
                            elevation={0}
                            ref={(el) => (itemRefs.current[idx] = el)}
                            onClick={() => {
                                history.push(
                                    linkProvider.react
                                        .gifts()
                                        .giftsGivenContentListing(
                                            u?.member_id,
                                            Tabs.AUDIOS,
                                            `${categoryId}-content`,
                                            { scrollTo: idx },
                                        ),
                                );
                            }}
                        >
                            <Box className={clsx(classes.flex)}>
                                <Typography>
                                    {u.first_name || ""} {u.last_name || ""}
                                </Typography>
                            </Box>
                            <Box
                                className={clsx(
                                    classes.flex,
                                    classes.alignCenter,
                                )}
                            >
                                <Typography className={clsx(classes.memberId)}>
                                    {u.member_id || ""}
                                </Typography>
                                <FontAwesomeIcon
                                    icon={["fas", "chevron-right"]}
                                />
                            </Box>
                        </Card>
                    ))}

                {loading && (
                    <AppBackdropProgress
                        open={true}
                        backdropText={messaging.loader.content}
                    />
                )}

                {loaded && !filteredMembers.length && (
                    <Typography
                        align="center"
                        variant="h6"
                        className={classes.noContent}
                    >
                        {messaging.common.noItemsFound}
                    </Typography>
                )}
            </div>
        </Layout>
    );
};

const useQueryParams = () => {
    const location = useLocation();
    const queryParams = useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });
        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo,
        };
    }, [location?.search]);

    return queryParams;
};
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            backgroundColor: theme.palette.background.default,
            color: theme.palette.grey[500],
        },
        wrapperLoader: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: `calc(100vh - ${theme.footer.height})`,
        },
        itemCard: {
            backgroundColor: theme.palette.background.default,
            display: "flex",
            justifyContent: "space-between",
            padding: "10px 15px",
            color: theme.palette.grey[500],
            "&:hover": {
                cursor: "pointer",
            },
        },
        quantityBubble: {
            marginRight: "12px",
            width: "30px",
            height: "30px",
            borderRadius: "100px",
            backgroundColor: theme.palette.grey[200],
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        },
        flex: {
            display: "flex",
        },
        alignCenter: {
            alignItems: "center",
        },
        memberId: {
            marginRight: "10px",
        },
        noContent: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "100%",
            color: `${theme.palette.common.black}`,
        },
    }),
);
